import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { NOTE_TYPES, formatDateTime } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blank = () => ({ note: '', note_type: 'General' });

const AcademicNotesPanel = ({ athlete, profile, canEdit, items, onChanged }) => {
  const [dlg, setDlg] = useState({ open: false, editing: null, data: blank() });
  const set = (patch) => setDlg(s => ({ ...s, data: { ...s.data, ...patch } }));

  const submit = async () => {
    if (!dlg.data.note?.trim()) { toast.error('Note text is required'); return; }
    try {
      if (dlg.editing) {
        await axios.put(`${API}/academic/notes/${dlg.editing.id}`, dlg.data);
        toast.success('Note updated');
      } else {
        await axios.post(`${API}/academic/notes`, {
          academic_profile_id: profile.id, athlete_id: athlete.id, ...dlg.data,
        });
        toast.success('Note added');
      }
      setDlg({ open: false, editing: null, data: blank() });
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/academic/notes/${id}`);
      toast.success('Note deleted');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-2" data-testid="academic-notes-list">
      <div className="flex justify-end">
        {canEdit && (
          <Button size="sm" onClick={() => setDlg({ open: true, editing: null, data: blank() })} data-testid="add-note-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Note
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">No notes yet.</p>
      ) : items.map(n => (
        <div key={n.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800" data-testid={`academic-note-${n.id}`}>
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-0">{n.note_type || 'General'}</Badge>
                <span className="text-[10px] text-gray-400">{formatDateTime(n.created_at)}</span>
              </div>
              <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 whitespace-pre-wrap">{n.note}</p>
            </div>
            {canEdit && (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setDlg({ open: true, editing: n, data: { note: n.note, note_type: n.note_type || 'General' } })} data-testid={`edit-note-${n.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(n.id)} data-testid={`delete-note-${n.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg(s => ({ ...s, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dlg.editing ? 'Edit Note' : 'Add Note'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={dlg.data.note_type} onValueChange={v => set({ note_type: v })}>
                <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{NOTE_TYPES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note *</Label>
              <Textarea rows={5} value={dlg.data.note} onChange={e => set({ note: e.target.value })} data-testid="note-text-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(s => ({ ...s, open: false }))}>Cancel</Button>
            <Button onClick={submit} data-testid="save-note-btn">{dlg.editing ? 'Save' : 'Add note'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademicNotesPanel;
